/**
 * RoPaginate - 分页组件 Web Component
 *
 * 功能特性：
 * - 基于 Web Components 标准开发
 * - 支持 data-current 和 data-total 属性控制分页状态
 * - 分页切换时抛出 change 事件
 * - 响应式设计：适配移动端和桌面端
 * - 与 RoDialog 保持一致的视觉风格
 * - 设计格式：<  current / total >
 *
 * 使用方法：
 * <ro-paginate data-current="1" data-total="10"></ro-paginate>
 *
 * 事件监听：
 * - change: 分页切换时触发，event.detail 包含 { current }
 */
class RoPaginate extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._current = 1;
    this._total = 1;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: flex;
          align-items: center;
          gap: 24px;
          font-size: 16px;
          line-height: 20px;
          color: #161616;
        }

        .nav-btn {
          box-sizing: border-box;
          width: 24px;
          height: 24px;
          min-width: 24px;
          min-height: 24px;
          padding: 0;
          margin: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 4px;
          border: none;
          background: transparent;
          color: inherit;
          cursor: pointer;
          transition: all 0.2s;
          /* iOS WKWebView 会给 button 默认内边距，挤压 SVG */
          -webkit-appearance: none;
          appearance: none;
        }

        .nav-btn svg {
          width: 7px;
          height: 12px;
          display: block;
          flex-shrink: 0;
        }

        .nav-btn:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.05);
        }

        .nav-btn:disabled {
          color: #6c6d6e;
          cursor: not-allowed;
        }

        .page-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .current-input {
          box-sizing: border-box;
          height: 32px;
          border: 1px solid #e1e1e1;
          border-radius: 4px;
          color: #161616;
          padding: 0 8px;
          text-align: center;
          font-size: 16px;
          line-height: 20px;
          /* 移除数字输入框的上下箭头 */
          -moz-appearance: textfield;
        }

        .current-input::-webkit-outer-spin-button,
        .current-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .current-input:focus {
          outline: none;
          border-color: #007bff;
        }

        .total-pages {
          color: #6c6d6e;
        }

        .separator {
          color: #6c6d6e;
        }
      </style>

      <button class="nav-btn prev" aria-label="Previous page" disabled>
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.59961 10.8486L0.599609 5.84863L5.59961 0.848633" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="page-info">
        <input type="number" class="current-input" value="1" min="1" max="1">
        <span class="separator">/</span>
        <span class="total-pages">1</span>
      </div>
      <button class="nav-btn next" aria-label="Next page" disabled>
      <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.847656 10.8486L5.84766 5.84863L0.847656 0.848633" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
      </svg>
      </button>
    `;

    this.prevBtn = this.shadowRoot.querySelector(".prev");
    this.nextBtn = this.shadowRoot.querySelector(".next");
    this.currentInput = this.shadowRoot.querySelector(".current-input");
    this.totalPagesEl = this.shadowRoot.querySelector(".total-pages");
  }

  connectedCallback() {
    this._setupEventListeners();
    this._updateUI();
  }

  static get observedAttributes() {
    return ["data-current", "data-total"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-current") {
      this._current = parseInt(newValue) || 1;
      this._updateUI();
    } else if (name === "data-total") {
      this._total = parseInt(newValue) || 1;
      this._updateUI();
    }
  }

  _setupEventListeners() {
    this.prevBtn.addEventListener("click", () => {
      if (this._current > 1) {
        this._setCurrentPage(this._current - 1);
      }
    });

    this.nextBtn.addEventListener("click", () => {
      if (this._current < this._total) {
        this._setCurrentPage(this._current + 1);
      }
    });

    this.currentInput.addEventListener("change", (e) => {
      const newPage = parseInt(e.target.value);
      if (!isNaN(newPage) && newPage >= 1 && newPage <= this._total) {
        this._setCurrentPage(newPage);
      } else {
        // 恢复原来的值
        e.target.value = this._current;
      }
    });

    this.currentInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        e.target.blur(); // 触发 change 事件
      }
    });
  }

  _updateUI() {
    // 更新页面信息
    this.currentInput.value = this._current;
    this.currentInput.max = this._total;
    this.totalPagesEl.textContent = this._total;

    // 更新按钮状态
    this.prevBtn.disabled = this._current <= 1;
    this.nextBtn.disabled = this._current >= this._total;
  }

  _setCurrentPage(page) {
    this._current = page;
    this.setAttribute("data-current", page);
    this._updateUI();

    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { current: this._current },
        bubbles: true,
      }),
    );
  }

  get current() {
    return this._current;
  }

  set current(value) {
    this.setAttribute("data-current", value);
  }

  get total() {
    return this._total;
  }

  set total(value) {
    this.setAttribute("data-total", value);
  }
}

if (!customElements?.get("ro-paginate")) {
  customElements.define("ro-paginate", RoPaginate);
}
